import { z } from 'zod';

// Contratos de "Roles y accesos" validados con Zod (ENG-VAL-01): un drift del
// backend cae al estado de error de la query, no revienta en render.

export const PERMISSION_GROUPS = ['documentos', 'catalogos', 'sistema'] as const;
export type PermissionGroup = (typeof PERMISSION_GROUPS)[number];

// `group` es string libre (no enum): si el backend agrega un grupo nuevo, la
// pantalla NO debe caer entera (H6). Los grupos desconocidos se ordenan al final
// bajo una etiqueta de reserva (buildPermissionMatrix), nunca se ocultan.
export const permissionCapabilitySchema = z.object({
  code: z.string(),
  label: z.string(),
  group: z.string(),
});
export type PermissionCapability = z.infer<typeof permissionCapabilitySchema>;

export const roleSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  permissions: z.array(z.string()),
});
export type Role = z.infer<typeof roleSchema>;

export const permissionsResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(permissionCapabilitySchema),
});

export const rolesResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(roleSchema),
});

/** Etiqueta legible de cada grupo de capacidades (encabezado de sección de la matriz). */
export const GROUP_LABELS: Record<PermissionGroup, string> = {
  documentos: 'Documentos',
  catalogos: 'Catálogos',
  sistema: 'Sistema',
};

// --- Panel C: accesos temporales de proveedor ---

export const GRANT_STATUSES = ['activo', 'por_vencer', 'vencido', 'revocado'] as const;
export type GrantStatus = (typeof GRANT_STATUSES)[number];

export const scopeGrantEntrySchema = z.object({
  module: z.string(),
  id: z.string(),
  actions: z.array(z.string()),
});

export const providerGrantSchema = z.object({
  id: z.string(),
  provider: z.object({
    userId: z.string(),
    name: z.string().nullable(),
    // Proveedor invitado desde el QMS (sin usuario en el LMS).
    external: z.boolean().default(false),
  }),
  grantedByName: z.string().nullable(),
  grantedAt: z.string(),
  expiresAt: z.string(),
  revokedAt: z.string().nullable(),
  revokedByName: z.string().nullable(),
  status: z.enum(GRANT_STATUSES),
  reason: z.string(),
  scope: z
    .object({ grants: z.array(scopeGrantEntrySchema) })
    .nullable()
    .catch(null),
});
export type ProviderGrant = z.infer<typeof providerGrantSchema>;

export const providerGrantsResponseSchema = z.object({
  success: z.literal(true),
  data: z.array(providerGrantSchema),
});

export const GRANT_STATUS_LABELS: Record<GrantStatus, string> = {
  activo: 'Activo',
  por_vencer: 'Por vencer',
  vencido: 'Vencido',
  revocado: 'Revocado',
};
